import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import { Router } from '@angular/router';
import {ScheduleService} from '../../services/schedule.service';
import {Entity} from '../../entity/entity';
import {StringUtils} from '../../utils/string-utils';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {OrganizationService} from '../../services/organization-service';
import {OrganizationPhoneService} from '../../services/organization-phone-service';
import {OrganizationPhone} from '../../organization/organization-phone';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RatingService} from '../../services/rating.service';
import {SelectionModelSizeCharacteristic} from '../../model/selection-model-size-characteristic';
import {RatingsModel} from '../../model/ratings-model';
import {RatingUnitService} from '../../services/rating-unit.service';
import {EntityLineService} from '../../services/entity-line-service';
import {RatingsMassUpdate} from '../../model/ratings-mass-update';
import {concatAll, firstValueFrom, from, Observable, Subject} from 'rxjs';
import {UnitRatingRequest} from '../../model/unit-rating-request';
import {UnitRatingResponse} from '../../model/unit-rating-response';
import {ScheduleLineInput} from '../../model/schedule-line-input';
import {RatingUnit} from '../../model/rating-unit';
import {RatingScheduleLineService} from '../../services/rating-schedule-line.service';
import {EntityLineUtils} from '../../utils/entity-line-utils';
import {ScheduleSharingInput} from '../../model/schedule-sharing-input';
import {PersonLoginService} from '../../services/person-login-service';
import {PersonLogin} from '../../person/person-login';
import {SelectionService} from '../../services/selection-service';
import {SelectionItem} from '../../selections/selection-item';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {FormsModule} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatHint, MatLabel} from '@angular/material/form-field';

import {MatIcon} from '@angular/material/icon';
import {MatAnchor, MatButton} from '@angular/material/button';
import {DialogService} from "../../services/dialog.service";
import {ScheduleSharingData} from "../../model/schedule-sharing-data";

@Component({
    selector: 'app-schedule-detail',
    templateUrl: './schedule-detail.component.html',
    styleUrls: ['./schedule-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatAnchor, MatIcon, MatButton, MatFormField, MatLabel, MatInput, FormsModule, MatHint, MatPaginator, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatProgressSpinner]
})
export class ScheduleDetailComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(ScheduleService);
  private readonly organizationService = inject(OrganizationService);
  private readonly organizationPhoneService = inject(OrganizationPhoneService);
  private readonly dialogService = inject(DialogService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly ratingService = inject(RatingService);
  private readonly ratingUnitService = inject(RatingUnitService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly ratingScheduleLineService = inject(RatingScheduleLineService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly selectionItemService = inject(SelectionService);
  readonly id = input.required<string>();


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly loadedSchedule = signal<Entity>(new Entity());
  scheduleLines: MatTableDataSource<EntityLineHeader> = new MatTableDataSource<EntityLineHeader>();
  readonly displayColumns = signal<string[]>(['modify', 'copy', 'delete', 'lineId', 'description', 'quantity', 'tags']);
  readonly controllingOrgName = signal<string>(undefined!);
  readonly controllingOrgPhone = signal<string>(undefined!);
  readonly requestedQuoteId = signal('None');
  readonly invalidRatingLineIds = signal<string[]>([]);
  readonly totalUnitCount = signal<number>(undefined!);
  private readonly userLogin = signal<PersonLogin>(undefined!);

  constructor() {
  }

  ngOnInit(): void {
    if (!this.id() || this.id().trim().length === 0) {
      this.backToList();
    } else {
      this.userLogin.set(this.personLoginService.getCachedLogin()!);
      this.loadScheduleDetail();
    }
  }

  private loadScheduleDetail(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.service.getSchedule(this.id()).subscribe({
      next: (schedule) => {
        this.loadedSchedule.set(schedule);
        this.totalUnitCount.set(this.getTotalUnitCount());
        if (this.allLinesAreSureflow()) {
          this.displayColumns.set(['lineId', 'description', 'quantity', 'tags']);
        }
        this.getInvalidRatingLines();
        this.scheduleLines = new MatTableDataSource<EntityLineHeader>(schedule.lines);
        this.scheduleLines.paginator = this.paginator;
        this.scheduleLines.sort = this.sort;
        this.service.getRequestedQuoteId(schedule.id.entityId).subscribe({next: (quoteId) => this.requestedQuoteId.set(quoteId)});
        this.organizationService.getOrganization(this.loadedSchedule().repOfficeId).subscribe({
          next: (repOffice) => {
            if ((repOffice?.controllingOrgId || '').length > 0) {
              this.organizationService.getOrganization(repOffice.controllingOrgId).subscribe({
                next: (controllingOrg) => this.controllingOrgName.set(controllingOrg.description)});
              this.organizationPhoneService.getOrganizationPhones(repOffice.controllingOrgId).subscribe({
                next: (phones: OrganizationPhone[]) => {
                  const primaryCodes: string[] = ['CORP', 'BUSNS1', 'BP1', 'WORK'];
                  const primaryContacts: OrganizationPhone[] =
                    phones.filter(phone => primaryCodes.indexOf(phone.relationshipTypeCode) >= 0);
                  this.controllingOrgPhone.set(primaryContacts.length > 0 ? primaryContacts[0].phoneNumber : 'Unknown');
                }
              });
            }
          }
        });
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)});
  }

  private getTotalUnitCount(): number {
    let unitCount: number = 0;
    if ((this.loadedSchedule()?.lines?.length || 0) > 0) {
      for (const line of this.loadedSchedule().lines) {
        unitCount += line.quantity;
      }
    }
    return unitCount;
  }

  isSureFlowUnitPresent(): boolean {
    if ((this.loadedSchedule()?.lines?.length || 0) > 0) {
      for (const line of this.loadedSchedule().lines) {
        if (this.isSureFlowUnit(line)) {
          return true;
        }
      }
    }
    return false;
  }

  openSureFlowLoopSimulator(): void {
    this.router.navigate(['schedules', 'sureflow', 'loops', this.loadedSchedule().id.entityId, this.loadedSchedule().id.entityRevision]);
  }

  private allLinesAreSureflow(): boolean {
    if ((this.loadedSchedule()?.lines?.length || 0) === 0) {
      return false;
    }
    for (const line of this.loadedSchedule().lines) {
      if (!this.isSureFlowUnit(line)) {
        return false;
      }
    }
    return true;
  }

  isSureFlowUnit(line: EntityLineHeader): boolean {
    const productId = line?.product || '';
    return productId.toUpperCase().indexOf('SUREFLOW') >= 0;
  }

  private getInvalidRatingLines(): void {
    this.invalidRatingLineIds.set([]);
    if ((this.loadedSchedule()?.lines?.length || 0) > 0) {
      for (const line of this.loadedSchedule().lines) {
        this.entityLineService.getFullEntityLine(line).subscribe({
          next: (fullLine) => {
            const ratingsAirflow: string = EntityLineUtils.getSpecificationCharacteristicValue(fullLine, "UNIT_RTO", "RCFAirflow");
            if (StringUtils.isZeroOrEmpty(+ratingsAirflow)) {
              this.invalidRatingLineIds.update(current => [...current, fullLine.lineId]);
            }
          }
        });
      }
    }
  }

  isInvalidLine(line: EntityLineHeader): boolean {
    return this.invalidRatingLineIds() && this.invalidRatingLineIds().indexOf(line.lineId) >= 0;
  }

  addQuickRatingLine(): void {
    this.router.navigate(['rating', 'scheduleUnitDetail', this.loadedSchedule().id.entityId, '0']);
  }

  editLine(line: EntityLineHeader): void {
    this.router.navigate(['rating', 'scheduleUnitDetail', line.key.entityId, line.lineId]);
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  private backToList(): void {
    this.router.navigate(['ratings', 'projectList']);
  }

  updateSchedule(): void {
    this.isBusy.set(true);
    this.service.updateSchedule(this.loadedSchedule()).subscribe({
      next: () => {
        this.loadScheduleDetail();
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)});
  }

  requestQuote(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.organizationService.getOrganization(this.userLogin().person.primaryOrgUnitId).subscribe({
      next: (engineeringOffice) => {
        if ((engineeringOffice?.controllingOrgId || '').trim().length > 0) {
          this.organizationService.getOrganization(engineeringOffice.controllingOrgId).subscribe({
            next: (repOffice) => {
              const repOrg = new SelectionItem();
              repOrg.code = repOffice.orgUnitId;
              repOrg.description = repOffice.description;
              const scheduleSharingData: ScheduleSharingData = new ScheduleSharingData();
              scheduleSharingData.user = this.userLogin().person.personId;
              scheduleSharingData.scheduleId = this.loadedSchedule().id.entityId;
              scheduleSharingData.organizations = [repOrg];
              scheduleSharingData.officeLabel = 'Rep Office';
              scheduleSharingData.userLabel = 'Please select a Rep User';
              scheduleSharingData.notificationLabel = 'A quote will be created for your rep office and the selected rep user will receive an E-mail notification';
              this.dialogService.showScheduleSharingDialog(scheduleSharingData).subscribe({
                next: (input: ScheduleSharingInput) => {
                  if (input) {
                    this.service.requestQuote(input).subscribe({
                      next: () => {
                        this.isBusy.set(false);
                        this.snackbar.open('A rep quote has been created', 'Close');
                        this.loadScheduleDetail();
                      },
                      error: (error) => this.handleError(error),
                      complete: () => this.isBusy.set(false)});
                  } else {
                    this.isBusy.set(false);
                  }
                }, error: (error) => this.handleError(error)
              });
            }, error: (error) => this.handleError(error)
          });
        } else {
          this.errorMessage.set('There is a problem with your Primary Organization ID. Please contact Customer Service');
        }
      }, error: (error) => this.handleError(error)
    });
  }

  shareWithEngineer(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.selectionItemService.getOrganizationEngineeringOffices(this.userLogin().person.primaryOrgUnitId).subscribe({
      next: (engineeringOffices) => {
        const scheduleSharingData: ScheduleSharingData = new ScheduleSharingData();
        scheduleSharingData.user = this.userLogin().person.personId;
        scheduleSharingData.scheduleId = this.loadedSchedule().id.entityId;
        scheduleSharingData.organizations = engineeringOffices;
        scheduleSharingData.officeLabel = 'Engineering Office';
        scheduleSharingData.userLabel = 'Please select an Engineer';
        scheduleSharingData.notificationLabel = 'This schedule will be shared with the selected engineering office user and they will receive an E-mail notification.  Any updates that they make WILL affect this schedule';
        this.dialogService.showScheduleSharingDialog(scheduleSharingData).subscribe({
          next: (input: ScheduleSharingInput) => {
            if (input) {
              this.service.shareSchedule(input).subscribe({
                next: () => {
                  this.isBusy.set(false);
                  this.snackbar.open('This schedule has been shared with the selected engineer', 'Close');
                },
                error: (error) => this.handleError(error),
                complete: () => this.isBusy.set(false)});
            } else {
              this.isBusy.set(false);
            }
          }, error: (error) => this.handleError(error),
        });
      }, error: (error) => this.handleError(error)
    });
  }

  isOutsideEngineer(): boolean {
    return this.personLoginService.isOutsideEngineer();
  }

  copyLine(line: EntityLineHeader): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.service.getScheduleLineDetail(line.key.entityId, line.lineId).subscribe({
      next: (detail) => {
        this.ratingService.getUnitRatingResponse(detail.candidate.ratingRequest).subscribe({
          next: (ratingResponse) => {
            const modelSize = new SelectionModelSizeCharacteristic();
            modelSize.ratingsModel = new RatingsModel();
            modelSize.ratingsModel.productId = detail.line.product;
            modelSize.modelCode = detail.candidate.ratingRequest.Model;
            modelSize.sizeCode = detail.candidate.ratingRequest.Size;
            modelSize.ratingsModel.modelCode = detail.candidate.ratingRequest.Model;
            detail.candidate.modelSizeCharacteristic = modelSize;
            detail.line.lineId = '0';
            detail.candidate.ratingResponse = ratingResponse;
            this.service.addLineToSchedule(detail).subscribe({
              next: (schedule) => {
                this.loadedSchedule.set(schedule);
                this.totalUnitCount.set(this.getTotalUnitCount());
                this.getInvalidRatingLines();
                this.scheduleLines = new MatTableDataSource<EntityLineHeader>(schedule.lines);
                this.scheduleLines.paginator = this.paginator;
                this.scheduleLines.sort = this.sort;
                this.isBusy.set(false);
              }, error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  openSelectionTool(): void {
    this.router.navigate(['ratings', 'selectionTool'], {queryParams: {scheduleId: this.loadedSchedule().id.entityId}});
  }

  openMassUpdateDialog(): void {
    if (this.scheduleLines.data.length > 1) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      this.buildMassUpdateData().subscribe({
        next: (updateData: RatingsMassUpdate) => {
          this.dialogService.showRatingsMassUpdateDialog(updateData).subscribe({
            next: (response: RatingsMassUpdate) => {
              if (response) {
                const scheduleLines = JSON.parse(JSON.stringify(this.loadedSchedule().lines));
                this.updateMassUpdateRatingResponses(response, scheduleLines).then(inputs => {
                  const updates: Observable<string>[] = this.saveSelectedUnits(inputs);
                  from(updates).pipe(concatAll()).subscribe({
                    complete: () => {
                      const errorResponses: ScheduleLineInput[] = [];
                      for (const input of inputs) {
                        if (StringUtils.isZeroOrEmpty(input.candidate?.ratingResponse?.Airflow)) {
                          errorResponses.push(input);
                        }
                      }
                      if (errorResponses.length > 0) {
                        this.showMassUpdateErrorDialog(errorResponses);
                      } else {
                        this.loadScheduleDetail();
                      }
                    }, error: (error) => this.handleError(error)
                  });
                });
              } else {
                this.isBusy.set(false);
              }
            }, error: (error) => this.handleError(error)
          });
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private showMassUpdateErrorDialog(errorUnits: ScheduleLineInput[]): void {
    this.dialogService.showRatingMassUpdateErrorsDialog(null!, errorUnits).subscribe({
      next: () => this.loadScheduleDetail(),
      error: (error) => this.handleError(error),
    });
  }

  private saveSelectedUnits(scheduleLines: ScheduleLineInput[]): Observable<string>[] {
    const requests: Observable<string>[] = [];
    for (const unit of scheduleLines) {
      requests.push(this.service.updateLine(unit));
    }
    return requests;
  }

  private async updateMassUpdateRatingResponses(response: RatingsMassUpdate, lines: EntityLineHeader[]): Promise<ScheduleLineInput[]> {
    const updatedUnits: Set<ScheduleLineInput> = new Set<ScheduleLineInput>();
    for (const id of response.hydronicCoolingLines) {
      const unit: ScheduleLineInput = await this.getMassUpdateUnit(id, updatedUnits, response, lines);
      unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringDryBulb = response.hydronicCoolingInputs.EnteringDryBulb;
      unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringWetBulb = response.hydronicCoolingInputs.EnteringWetBulb;
      unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringFluidTemperature = response.hydronicCoolingInputs.EnteringFluidTemperature;
      unit.candidate.ratingRequest.PrimaryCoilRequest.FluidType = response.hydronicCoolingInputs.FluidType;
      unit.candidate.ratingRequest.PrimaryCoilRequest.GlycolPercentage = response.hydronicCoolingInputs.GlycolPercentage;
      unit.candidate.ratingRequest.PrimaryCoilRequest.TemperatureChange = response.hydronicCoolingInputs.TemperatureChange;
      unit.candidate.ratingRequest.PrimaryCoilRequest.FlowRate = response.hydronicCoolingInputs.FlowRate;
      updatedUnits.add(unit);
    }
    for (const id of response.hydronicHeatingLines) {
      const unit: ScheduleLineInput = await this.getMassUpdateUnit(id, updatedUnits, response, lines);
      if (unit.candidate.ratingRequest.SecondaryCoilRequest) {
        unit.candidate.ratingRequest.SecondaryCoilRequest.EnteringDryBulb = response.hydronicHeatingInputs.EnteringDryBulb;
        unit.candidate.ratingRequest.SecondaryCoilRequest.EnteringFluidTemperature = response.hydronicHeatingInputs.EnteringFluidTemperature;
        unit.candidate.ratingRequest.SecondaryCoilRequest.FluidType = response.hydronicHeatingInputs.FluidType;
        unit.candidate.ratingRequest.SecondaryCoilRequest.GlycolPercentage = response.hydronicHeatingInputs.GlycolPercentage;
        unit.candidate.ratingRequest.SecondaryCoilRequest.TemperatureChange = response.hydronicHeatingInputs.TemperatureChange;
        unit.candidate.ratingRequest.SecondaryCoilRequest.FlowRate = response.hydronicHeatingInputs.FlowRate;
      } else {
        unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringDryBulb = response.hydronicHeatingInputs.EnteringDryBulb;
        unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringFluidTemperature = response.hydronicHeatingInputs.EnteringFluidTemperature;
        unit.candidate.ratingRequest.PrimaryCoilRequest.FluidType = response.hydronicHeatingInputs.FluidType;
        unit.candidate.ratingRequest.PrimaryCoilRequest.GlycolPercentage = response.hydronicHeatingInputs.GlycolPercentage;
        unit.candidate.ratingRequest.PrimaryCoilRequest.TemperatureChange = response.hydronicHeatingInputs.TemperatureChange;
        unit.candidate.ratingRequest.PrimaryCoilRequest.FlowRate = response.hydronicHeatingInputs.FlowRate;
      }
      updatedUnits.add(unit);
    }
    for (const id of response.refrigerantLines) {
      const unit: ScheduleLineInput = await this.getMassUpdateUnit(id, updatedUnits, response, lines);
      unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringDryBulb = response.refrigerantInputs.EnteringDryBulb;
      unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringWetBulb = response.refrigerantInputs.EnteringWetBulb;
      unit.candidate.ratingRequest.PrimaryCoilRequest.SaturationSuctionTemperature = response.refrigerantInputs.SaturationSuctionTemperature;
      updatedUnits.add(unit);
    }
    for (const id of response.steamLines) {
      const unit: ScheduleLineInput = await this.getMassUpdateUnit(id, updatedUnits, response, lines);
      if (unit.candidate.ratingRequest.SecondaryCoilRequest) {
        unit.candidate.ratingRequest.SecondaryCoilRequest.EnteringDryBulb = response.steamInputs.EnteringDryBulb;
        unit.candidate.ratingRequest.SecondaryCoilRequest.EnteringSteamPressure = response.steamInputs.EnteringSteamPressure;
      } else {
        unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringDryBulb = response.steamInputs.EnteringDryBulb;
        unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringSteamPressure = response.steamInputs.EnteringSteamPressure;
      }
      updatedUnits.add(unit);
    }
    for (const id of response.electricHeatLines) {
      const unit: ScheduleLineInput = await this.getMassUpdateUnit(id, updatedUnits, response, lines);
      unit.candidate.ratingRequest.ElectricHeatRequest.EnteringDryBulb = response.electricHeatInputs.EnteringDryBulb;
      updatedUnits.add(unit);
    }
    const updated: ScheduleLineInput[] = [];
    for (const unit of updatedUnits) {
      await this.updateLineRatings(unit.candidate.ratingRequest).then(response => {
        unit.candidate.ratingResponse = response;
        updated.push(unit);
      });
    }
    return updated;
  }

  private async getMassUpdateUnit(id: number, updatedUnits: Set<ScheduleLineInput>, response: RatingsMassUpdate, lines: EntityLineHeader[]): Promise<ScheduleLineInput> {
    const existingUpdate = [...updatedUnits].filter(unit => +unit.line.lineId === id);
    let unit: ScheduleLineInput;
    if (existingUpdate.length === 1) {
      unit = existingUpdate[0];
    } else {
      const header: EntityLineHeader = lines.filter(line => +line.lineId === id)[0];
      unit = await this.getScheduleLineInput(header);
      unit.candidate.ratingRequest.ExternalStaticPressure = response.externalStaticPressure;
      unit.candidate.ratingRequest.Altitude = response.altitude;
      unit.candidate.ratingRequest.IsMetricInput = response.isMetric;
      const modelSize = new SelectionModelSizeCharacteristic();
      modelSize.ratingsModel = new RatingsModel();
      modelSize.ratingsModel.productId = header.product;
      modelSize.modelCode = unit.candidate.ratingRequest.Model;
      modelSize.sizeCode = unit.candidate.ratingRequest.Size;
      modelSize.ratingsModel.modelCode = unit.candidate.ratingRequest.Model;
      unit.candidate.modelSizeCharacteristic = modelSize;
    }
    return unit;
  }

  private async updateLineRatings(request: UnitRatingRequest): Promise<UnitRatingResponse> {
    return await firstValueFrom<UnitRatingResponse>(this.ratingService.getUnitRatingResponse(request));
  }

  private buildMassUpdateData(): Observable<RatingsMassUpdate> {
    const subject: Subject<RatingsMassUpdate> = new Subject<RatingsMassUpdate>();
    this.loadMassUpdateInfo().then(info => subject.next(info), error => subject.error(error));
    return subject.asObservable();
  }

  private async getScheduleLineInput(header: EntityLineHeader): Promise<ScheduleLineInput> {
    return await firstValueFrom<ScheduleLineInput>(this.service.getScheduleLineDetail(header.key.entityId, header.lineId));
  }

  private async loadMassUpdateInfo(): Promise<RatingsMassUpdate> {
    const updateData: RatingsMassUpdate = new RatingsMassUpdate();
    for (const line of this.loadedSchedule().lines) {
      const unit = await this.getScheduleLineInput(line);
      updateData.isMetric = unit.candidate.ratingRequest.IsMetricInput;
      updateData.externalStaticPressure = unit.candidate.ratingRequest.ExternalStaticPressure;
      updateData.altitude = unit.candidate.ratingRequest.Altitude;
      if (!StringUtils.isEmptyNumericValue(unit.candidate.ratingRequest.PrimaryCoilRequest?.SaturationSuctionTemperature)) {
        updateData.refrigerantLines.push(+unit.line.lineId);
        updateData.refrigerantInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.PrimaryCoilRequest));
        updateData.refrigerantInputs.FluidType = undefined!;
        updateData.refrigerantInputs.isRefrigerant = true;
        updateData.refrigerantInputs.isCooling = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.candidate.ratingRequest.PrimaryCoilRequest?.EnteringSteamPressure)) {
        updateData.steamLines.push(+unit.line.lineId);
        updateData.steamInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.PrimaryCoilRequest));
        updateData.steamInputs.FluidType = undefined!;
        updateData.steamInputs.isSteam = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.candidate.ratingRequest.PrimaryCoilRequest?.EnteringFluidTemperature)) {
        if (unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringFluidTemperature > unit.candidate.ratingRequest.PrimaryCoilRequest.EnteringDryBulb) {
          updateData.hydronicHeatingLines.push(+unit.line.lineId);
          updateData.hydronicHeatingInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.PrimaryCoilRequest));
          updateData.hydronicHeatingInputs.isFluid = true;
        } else {
          updateData.hydronicCoolingLines.push(+unit.line.lineId);
          updateData.hydronicCoolingInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.PrimaryCoilRequest));
          updateData.hydronicCoolingInputs.isFluid = true;
          updateData.hydronicCoolingInputs.isCooling = true;
        }
      }
      if (!StringUtils.isEmptyNumericValue(unit.candidate.ratingRequest.SecondaryCoilRequest?.EnteringSteamPressure)) {
        updateData.steamLines.push(+unit.line.lineId);
        updateData.steamInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.SecondaryCoilRequest));
        updateData.steamInputs.isSteam = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.candidate.ratingRequest.SecondaryCoilRequest?.EnteringFluidTemperature)) {
        updateData.hydronicHeatingLines.push(+unit.line.lineId);
        updateData.hydronicHeatingInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.SecondaryCoilRequest));
        updateData.hydronicHeatingInputs.isFluid = true;
      }
      if (!StringUtils.isEmptyNumericValue(+unit.candidate.ratingRequest.ElectricHeatRequest?.ElectricHeatKilowatts)) {
        updateData.electricHeatLines.push(+unit.line.lineId);
        updateData.electricHeatInputs = JSON.parse(JSON.stringify(unit.candidate.ratingRequest.ElectricHeatRequest));
      }
    }
    return updateData;
  }

  openWorkingUnitSelectionDialog(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.ratingUnitService.getUnits().subscribe({
      next: (units: RatingUnit[]) => {
        this.dialogService.showWorkingUnitSelectionDialog(units).subscribe({
          next: (units: RatingUnit[]) => {
            if (units) {
              this.addScheduleLines(units).then(() => this.ngOnInit());
            } else {
              this.isBusy.set(false);
            }
          },
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private async addScheduleLines(units: RatingUnit[]): Promise<void> {
    for (const unit of units) {
      await this.addLineToSchedule(unit);
    }
  }

  private async addLineToSchedule(unit: RatingUnit): Promise<string> {
    return await firstValueFrom<string>(this.ratingScheduleLineService.addScheduleLine(this.loadedSchedule(), unit, [], 1));
  }

  deleteLine(line: EntityLineHeader): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.service.deleteLine(this.loadedSchedule().id.entityId, line.lineId).subscribe({
      complete: () => this.loadScheduleDetail(),
      error: (error) => this.handleError(error)
    });
  }



}
